import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../apiClientContext";
import { fetchNextWorkItem } from "./api";

// Caches the press touches: serving an item locks it, which shifts the
// lock fields on every list/detail surface that renders it. The realtime
// WorkItemLocked event already patches these caches in place for every
// connected client, so this invalidation is the belt-and-braces path for
// the presser's own tab (covering the window before the push round-trips,
// and the degraded case where realtime is down). Declared locally to keep
// the dependency arrow one-way rather than importing key constants across
// features.
const WORKITEM_LIST_KEY = ["workitems"] as const;
const QUEUE_TALLIES_KEY = ["queues", "tallies"] as const;

/**
 * The "Next" dispenser, modeled as an imperative action.
 *
 * A press is intent, not a subscription: it must fire on click, may serve
 * a different item each time, and mutates server state (the lock). That is
 * exactly a mutation, so it lives here as `useMutation` even though the
 * underlying call is a `GET`. The component owns navigation and the
 * outcome toast in its own `onSuccess`; this hook only reconciles caches
 * that the resulting lock invalidates.
 */
export function useNextWorkItem() {
    const client = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => fetchNextWorkItem(client),
        onSuccess: (result) => {
            // Only a real serve (or re-serve) changes lock state; "nothing
            // to do" leaves every cache untouched, so skip the churn.
            if (result.item === null) {
                return;
            }
            void queryClient.invalidateQueries({ queryKey: WORKITEM_LIST_KEY });
            void queryClient.invalidateQueries({ queryKey: QUEUE_TALLIES_KEY });
        },
    });
}
